import * as React from 'react';

import moment from 'moment';

// import { Translate } from 'react-localize-redux';

import { CalendarTiers } from '@webapp/sdk/plans';
import { getSelectedTier } from '@webapp/utils/plans/get-selected-tiers';

import {
  // ChipSelector,
  // FilterSelector,
  IconWrapper,
  // Separator,
  YearSelectorWrapper,
} from './components';

import DatepickerYears from './datepicker-years';
import DatepickerPrices from './datepicker-prices';
import DatepickerMonths from './datepicker-months';
import DatepickerControls from './datepicker-controls';
import DatepickerCalendar from './datepicker-calendar';

import Flex from '@components/layout/flex';
import Typography from '@components/basics/typography';
import CloseIcon from '@components/basics/icons/close';
import DateIcon from '@components/basics/icons/date-icon';
import CircleArrowIcon from '@components/basics/icons/circle-arrow';

import Dropdown from '@webapp/controllers/form/dropdown';
import { useMemo } from 'react';

interface DatepickerProps {
  id?: string;
  className?: any;
  title?: React.ReactNode;
  value?: string;
  active?: moment.Moment;
  min?: moment.Moment;
  max?: moment.Moment;
  priceTiers?: CalendarTiers['priceTiers'];
  customBorderRadius?: string;
  hasPillShape?: boolean;
  isMonthSelector?: boolean;
  hasChanges?: boolean;
  onChange: (value: string) => void;
}

export const Datepicker: React.FC<DatepickerProps> = ({
  min = moment().subtract(100, 'years'),
  max = moment().add(2, 'years'),
  value,
  ...props
}) => {
  const activeMoment = (props.active || moment(new Date())).startOf('month');

  const [active, setActive] = React.useState(activeMoment);
  const [isYearMenuOpen, setIsYearMenuOpen] = React.useState(false);
  const [menuClosed, setMenuClosed] = React.useState(false);

  const [filter] = React.useState<'monthly' | 'annually'>('monthly');

  const moveNextMonth = () => setActive(moment(active).add(1, 'month'));
  const movePrevMonth = () => setActive(moment(active).subtract(1, 'month'));

  // const getSelected = () => (props.value ? moment(props.value) : undefined);
  const selectedDay = useMemo(() => {
    return value ? moment(value) : undefined;
  }, [value]);

  const formatValue = () => selectedDay?.format('LL');

  // const formatMonthValue = () => moment(props.value).format('MMM YYYY');
  const formatMonthValue = () => moment(active).format('MMM YYYY');

  const handleChange = (day: moment.Moment) => {
    setActive(day);
    setMenuClosed(true);
    props.onChange(day.toISOString());
  };

  const handleAction = (action: string) => {
    switch (action) {
      case 'next:month':
        return moveNextMonth();
      case 'prev:month':
        return movePrevMonth();
      default:
        return setIsYearMenuOpen(!isYearMenuOpen);
    }
  };

  const handleYear = (year: number) => {
    const newDate = moment(active).set('year', year);
    setActive(newDate);
    setIsYearMenuOpen(false);
    props.onChange(newDate.toISOString());
  };

  const handleMonth = (month: number) => {
    const newDate = moment(active).set('month', month);
    setActive(newDate);
    props.onChange(newDate.toISOString());
  };

  React.useEffect(() => {
    const newMoment = (props.active || moment(new Date())).startOf('month');
    setActive(newMoment);
  }, [props.active]);

  return (
    <Dropdown
      id={props.id}
      className={props.className}
      title={props.title}
      value={value? value : props.isMonthSelector ? formatMonthValue() : formatValue()}
      contentType="datepicker"
      customBorderRadius={props.customBorderRadius}
      noMaxHeight={!isYearMenuOpen || props.hasPillShape}
      hasPillShape={props.hasPillShape}
      decoration={
        props.hasPillShape ? (
          <DateIcon isInvertedTheme={props.hasChanges} />
        ) : undefined
      }
      hasChanges={props.hasChanges}
      customIcon={
        !props.hasPillShape && (
          <IconWrapper>
            <DateIcon />
          </IconWrapper>
        )
      }
      onOpen={() => setMenuClosed(false)}
      closeMenu={menuClosed}
    >
      {!props.isMonthSelector ? (
        <DatepickerControls
          active={active}
          isYearMenuOpen={isYearMenuOpen}
          onChange={(action) => handleAction(action)}
        />
      ) : (
        // TODO: DEVTEAM-727 - When the user opens the year selector this is a step prior of selecting a month so the user can select for example September 2020.
        <YearSelectorWrapper justifyContent="space-between" alignItems="center">
          <Flex
            className="year-dropdown"
            justifyContent="flex-start"
            alignItems="center"
            gap={5}
            onClick={() => setIsYearMenuOpen(!isYearMenuOpen)}
          >
            <Typography
              className={`year-text ${props.hasChanges ? 'has-changes' : ''}`}
              weight="700"
            >
              {moment(active).year()}
            </Typography>
            <CircleArrowIcon size="16" isDark arrowUp={isYearMenuOpen} />
          </Flex>
          <CloseIcon size={24} isDark />
        </YearSelectorWrapper>
      )}
      {!props.isMonthSelector &&
        (!isYearMenuOpen ? (
          <>
            <DatepickerCalendar
              active={active}
              selected={selectedDay}
              min={min}
              max={max}
              onChange={(day: moment.Moment) => handleChange(day)}
              priceTiers={props.priceTiers}
            />
            {props.priceTiers?.length ? (
              <DatepickerPrices
                priceTiers={props.priceTiers}
                selected={getSelectedTier(props.priceTiers, value ?? undefined)}
              />
            ) : null}
          </>
        ) : (
          <DatepickerYears
            onChange={(year) => handleYear(year)}
            currentYear={moment(active).year()}
            maxYear={max.year()}
            minYear={min.year()}
            isDark={props.hasPillShape}
          />
        ))}
      {props.isMonthSelector && (
        <>
          {/* TODO: DEVTEAM-727 - DatepickerMonths needs to be integrated so when when user selects a month it will only show that month. */}
          {filter === 'monthly' && !isYearMenuOpen ? (
            <DatepickerMonths
              onChange={(month) => handleMonth(month)}
              currentMonth={moment(active).month()}
              isDark={props.hasPillShape}
            />
          ) : (
            // TODO: DEVTEAM-727 - When the user selects a year we need to show the whole year.
            <DatepickerYears
              onChange={(year) => handleYear(year)}
              currentYear={moment(active).year()}
              maxYear={max.year()}
              minYear={min.year()}
              isDark={props.hasPillShape}
            />
          )}
          {/* <Separator /> */}
          {/* <FilterSelector>
            <ChipSelector
              className={filter === 'monthly' ? 'active' : ''}
              weight="700"
              onClick={() => setFilter('monthly')}
            >
              <Translate id="DATEPICKER.MONTHLY" />
            </ChipSelector>
            <ChipSelector
              className={filter === 'annually' ? 'active' : ''}
              weight="700"
              onClick={() => setFilter('annually')}
            >
              <Translate id="DATEPICKER.ANNUALLY" />
            </ChipSelector>
          </FilterSelector> */}
        </>
      )}
    </Dropdown>
  );
};

export default Datepicker;
